import React, { useState, useEffect } from 'react';
import { stripeConnectApi, getFacilityBusinessHours, guestApi } from '../api';
import { SubscriptionPlan, PublicPricingSettings } from '../api_types';
import { useAuth } from '../contexts/AuthContext';
import { format, parseISO } from 'date-fns';
import { GuestBooking, PublicAccessSettings } from '../api_types';
import LoadingSpinner from '../shared/components/LoadingSpinner';

interface PaidPublicUseProps {
  businessId: string;
  facilityId: string;
  stripeAccountId?: string;
}

const analyticsStyles = {
  card: "bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200",
  cardHeader: "text-sm font-medium text-gray-500 mb-1",
  cardValue: "text-2xl font-semibold text-gray-900",
  sectionTitle: "text-lg font-medium text-gray-900 mb-4",
  table: "min-w-full",
  tableHeader: "text-left text-sm font-medium text-gray-500 pb-3 border-b border-gray-200",
  tableCell: "py-3 text-sm text-gray-900 border-b border-gray-100",
  select: "mt-1 block w-48 pl-3 pr-10 py-2 text-base border-gray-200 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md",
};

const PaidPublicUse: React.FC<PaidPublicUseProps> = ({ businessId, facilityId, stripeAccountId: propStripeAccountId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publicAccess, setPublicAccess] = useState<PublicAccessSettings | null>(null);
  const [pricingSettings, setPricingSettings] = useState<PublicPricingSettings>({
    weekendRate: 0,
    weekdayPeakRate: 0,
    weekdayOffPeakRate: 0,
    peakHours: {
      startTime: '17:00',
      endTime: '21:00'
    },
    updatedAt: new Date().toISOString()
  });
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(propStripeAccountId || null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [newPlanData, setNewPlanData] = useState({
    name: '',
    price: '',
  });
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    price: '',
  });
  const [activeTab, setActiveTab] = useState<'settings' | 'analytics'>('settings');
  const [bookings, setBookings] = useState<GuestBooking[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'year'>('month');
  const { user } = useAuth();
  const [showSuccess, setShowSuccess] = useState(false);
  const [businessHours, setBusinessHours] = useState<{ open: string; close: string }>({ open: '08:00', close: '21:00' });
  const [showStartTimeDropdown, setShowStartTimeDropdown] = useState(false);
  const [showEndTimeDropdown, setShowEndTimeDropdown] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch public access settings
        const data = await guestApi.getPublicAccessSettings(businessId, facilityId);
        setPublicAccess(data);
        setStripeAccountId(data.stripeAccountId || null);

        // Fetch facility business hours
        const hours = await getFacilityBusinessHours(businessId, facilityId);
        // Find earliest open and latest close times across all days
        const openTimes = Object.values(hours)
          .filter(day => !day.isClosed)
          .map(day => day.openTime);
        const closeTimes = Object.values(hours)
          .filter(day => !day.isClosed)
          .map(day => day.closeTime);
        
        if (openTimes.length > 0 && closeTimes.length > 0) {
          const earliestOpen = openTimes.reduce((a, b) => a < b ? a : b);
          const latestClose = closeTimes.reduce((a, b) => a > b ? a : b);
          setBusinessHours({ open: earliestOpen, close: latestClose });
        }

        // Fetch pricing settings
        try {
          const pricingData = await guestApi.getPublicPricingSettings(businessId, facilityId);
          setPricingSettings(pricingData);
        } catch (pricingError) {
          console.error('Error fetching pricing settings:', pricingError);
          // Set default pricing settings if fetch fails
          setPricingSettings({
            weekendRate: 0,
            weekdayPeakRate: 0,
            weekdayOffPeakRate: 0,
            peakHours: {
              startTime: '17:00',
              endTime: '21:00'
            },
            updatedAt: new Date().toISOString()
          });
        }

        // Fetch recent bookings
        try {
          const bookingsData = await guestApi.getGuestBookings(businessId, facilityId);
          setBookings(bookingsData);
        } catch (bookingsError) {
          console.error('Error fetching bookings:', bookingsError);
          setBookings([]);
        }
      } catch (err) {
        console.error('Error in fetchData:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [facilityId]);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setAnalyticsLoading(true);
        const bookingsData = await guestApi.getGuestBookings(businessId, facilityId);
        setBookings(bookingsData);
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError('Failed to load booking data');
      } finally {
        setAnalyticsLoading(false);
      }
    };

    if (activeTab === 'analytics') {
      fetchBookings();
    }
  }, [facilityId, activeTab]);

  const handleTogglePublicAccess = async () => {
    if (!publicAccess) {
      setError('Facility data not loaded');
      return;
    }

    const currentData = publicAccess; // Type guard to ensure publicAccess is not null

    try {
      await guestApi.updatePublicAccessSettings(businessId, facilityId, {
        openToPublic: !currentData.openToPublic
      });

      // Update local state
      setPublicAccess({
        ...currentData,
        openToPublic: !currentData.openToPublic,
      });
    } catch (err) {
      console.error(err);
      setError('Failed to update facility settings');
    }
  };

  const handleUpdatePricing = async () => {
    try {
      setError(null);
      setShowSuccess(false);

      // Validate peak hours against business hours
      const peakStart = pricingSettings.peakHours.startTime;
      const peakEnd = pricingSettings.peakHours.endTime;
      
      if (peakStart < businessHours.open || peakEnd > businessHours.close) {
        setError(`Peak hours must be within business hours (${businessHours.open} - ${businessHours.close})`);
        return;
      }

      await stripeConnectApi.updatePublicPricingSettings(businessId, facilityId, pricingSettings);
      
      // Show success indicator
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000); // Hide after 3 seconds
      
    } catch (err) {
      console.error(err);
      setError('Failed to update pricing settings. Please try again.');
    }
  };

  // Analytics metrics
  const totalBookings = bookings.length;
  const totalRevenue = bookings.reduce((sum, b) => sum + b.price, 0);
  const averageBookingValue = totalBookings > 0 ? totalRevenue / totalBookings : 0;
  const confirmedBookings = bookings.filter(b => b.status === 'confirmed').length;
  const onlinePayments = bookings.filter(b => b.paymentMethod === 'online').length;
  const onsitePayments = bookings.filter(b => b.paymentMethod === 'onsite').length;

  // Group by date
  const bookingsByDate = bookings.reduce((acc, b) => {
    acc[b.date] = (acc[b.date] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const revenueByDate = bookings.reduce((acc, b) => {
    acc[b.date] = (acc[b.date] || 0) + b.price;
    return acc;
  }, {} as Record<string, number>);

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  const formatTime = (time: string) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  // Generate time slots within business hours
  const generateTimeSlots = (startTime: string, endTime: string) => {
    const slots = [];
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        // Skip if we're past the end time
        if (hour === endHour && minute > endMinute) continue;
        // Skip if we're before the start time
        if (hour === startHour && minute < startMinute) continue;
        
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push({
          display: formatTime(time),
          value: time
        });
      }
    }
    return slots;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-xl text-red-500">{error}</div>
      </div>
    );
  }

  if (!stripeAccountId) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-xl text-gray-900">Paid Public Access Setup</div>
        <div className="mt-4 text-gray-600">
          Please set up Stripe on the payments tab to enable public access for your facility.
        </div>
      </div>
    );
  }

  if (!publicAccess) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-xl text-red-500">Failed to load facility data</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pl-8 pr-4 md:pl-16 md:pr-8 pt-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('settings')}
            className={`${
              activeTab === 'settings'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Settings
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`${
              activeTab === 'analytics'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Analytics
          </button>
        </nav>
      </div>

      {/* Settings Tab Content */}
      {activeTab === 'settings' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-xl text-gray-900">Public Access Setup</div>
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Allow Public Access</h3>
                <p className="text-sm text-gray-500">
                  Enable this to allow non-members to book courts at your facility.
                </p>
              </div>
              <button
                onClick={handleTogglePublicAccess}
                className={`${
                  publicAccess.openToPublic ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
              >
                <span className="sr-only">Toggle public access</span>
                <span
                  className={`${
                    publicAccess.openToPublic ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
                >
                  <span
                    className={`${
                      publicAccess.openToPublic
                        ? 'opacity-0 ease-out duration-100'
                        : 'opacity-100 ease-in duration-200'
                    } absolute inset-0 h-full w-full flex items-center justify-center transition-opacity`}
                    aria-hidden="true"
                  >
                    <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 12 12">
                      <path
                        d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span
                    className={`${
                      publicAccess.openToPublic
                        ? 'opacity-100 ease-in duration-200'
                        : 'opacity-0 ease-out duration-100'
                    } absolute inset-0 h-full w-full flex items-center justify-center transition-opacity`}
                    aria-hidden="true"
                  >
                    <svg className="h-3 w-3 text-indigo-600" fill="currentColor" viewBox="0 0 12 12">
                      <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                    </svg>
                  </span>
                </span>
              </button>
            </div>
          </div>

          {/* Pricing Settings */}
          {publicAccess.openToPublic && (
            <div className="mt-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing Settings</h3>
              
              <div className="space-y-6">
                {/* Weekend Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Weekend Rate (per hour)
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      value={pricingSettings.weekendRate / 100}
                      onChange={(e) => setPricingSettings({
                        ...pricingSettings,
                        weekendRate: Math.round(parseFloat(e.target.value) * 100)
                      })}
                      className="pl-7 block w-full border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Weekday Peak Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Weekday Peak Rate (per hour)
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      value={pricingSettings.weekdayPeakRate / 100}
                      onChange={(e) => setPricingSettings({
                        ...pricingSettings,
                        weekdayPeakRate: Math.round(parseFloat(e.target.value) * 100)
                      })}
                      className="pl-7 block w-full border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Weekday Off-Peak Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Weekday Off-Peak Rate (per hour)
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      value={pricingSettings.weekdayOffPeakRate / 100}
                      onChange={(e) => setPricingSettings({
                        ...pricingSettings,
                        weekdayOffPeakRate: Math.round(parseFloat(e.target.value) * 100)
                      })}
                      className="pl-7 block w-full border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Peak Hours */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Peak Hours
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500">Start Time</label>
                      <div className="relative">
                        <div 
                          onClick={() => setShowStartTimeDropdown(!showStartTimeDropdown)}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm cursor-pointer"
                        >
                          {formatTime(pricingSettings.peakHours.startTime)}
                        </div>
                        {showStartTimeDropdown && (
                          <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                            {generateTimeSlots(businessHours.open, businessHours.close).map((slot) => (
                              <div
                                key={slot.value}
                                onClick={() => {
                                  setPricingSettings({
                                    ...pricingSettings,
                                    peakHours: {
                                      ...pricingSettings.peakHours,
                                      startTime: slot.value
                                    }
                                  });
                                  setShowStartTimeDropdown(false);
                                }}
                                className={`p-2 text-sm cursor-pointer hover:bg-gray-50 ${
                                  pricingSettings.peakHours.startTime === slot.value ? 'bg-indigo-50 text-indigo-600' : ''
                                }`}
                              >
                                {slot.display}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">End Time</label>
                      <div className="relative">
                        <div 
                          onClick={() => setShowEndTimeDropdown(!showEndTimeDropdown)}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm cursor-pointer"
                        >
                          {formatTime(pricingSettings.peakHours.endTime)}
                        </div>
                        {showEndTimeDropdown && (
                          <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                            {generateTimeSlots(pricingSettings.peakHours.startTime, businessHours.close).map((slot) => (
                              <div
                                key={slot.value}
                                onClick={() => {
                                  setPricingSettings({
                                    ...pricingSettings,
                                    peakHours: {
                                      ...pricingSettings.peakHours,
                                      endTime: slot.value
                                    }
                                  });
                                  setShowEndTimeDropdown(false);
                                }}
                                className={`p-2 text-sm cursor-pointer hover:bg-gray-50 ${
                                  pricingSettings.peakHours.endTime === slot.value ? 'bg-indigo-50 text-indigo-600' : ''
                                }`}
                              >
                                {slot.display}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end items-center space-x-4">
                  {showSuccess && (
                    <div className="flex items-center text-green-600">
                      <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Settings updated successfully</span>
                    </div>
                  )}
                  <button
                    onClick={handleUpdatePricing}
                    className={`px-4 py-2 rounded-md text-white font-medium ${
                      'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    Save Pricing Settings
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab Content */}
      {activeTab === 'analytics' && (
        <div className="space-y-8 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-gray-900">Guest Booking Analytics</h2>
            <select
              value={dateRange}
              onChange={e => setDateRange(e.target.value as any)}
              className={analyticsStyles.select}
            >
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
              <option value="year">Last Year</option>
            </select>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className={`${analyticsStyles.card} p-6`}>
              <div className={analyticsStyles.cardHeader}>Total Bookings</div>
              <div className={analyticsStyles.cardValue}>{totalBookings}</div>
              <div className="mt-2 text-sm text-gray-500">
                {dateRange === 'week' ? 'This week' : dateRange === 'month' ? 'This month' : 'This year'}
              </div>
            </div>
            <div className={`${analyticsStyles.card} p-6`}>
              <div className={analyticsStyles.cardHeader}>Total Revenue</div>
              <div className={analyticsStyles.cardValue}>{formatPrice(totalRevenue)}</div>
              <div className="mt-2 text-sm text-gray-500">
                {onlinePayments + onsitePayments} payments
              </div>
            </div>
            <div className={`${analyticsStyles.card} p-6`}>
              <div className={analyticsStyles.cardHeader}>Avg. Booking Value</div>
              <div className={analyticsStyles.cardValue}>{formatPrice(averageBookingValue)}</div>
              <div className="mt-2 text-sm text-gray-500">Per reservation</div>
            </div>
            <div className={`${analyticsStyles.card} p-6`}>
              <div className={analyticsStyles.cardHeader}>Confirmation Rate</div>
              <div className={analyticsStyles.cardValue}>
                {totalBookings > 0 ? ((confirmedBookings / totalBookings) * 100).toFixed(1) : 0}%
              </div>
              <div className="mt-2 text-sm text-gray-500">{confirmedBookings} confirmed</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Bookings Over Time */}
            <div className={`${analyticsStyles.card} p-6`}>
              <h3 className={analyticsStyles.sectionTitle}>Bookings Over Time</h3>
              <div className="overflow-x-auto">
                <table className={analyticsStyles.table}>
                  <thead>
                    <tr>
                      <th className={analyticsStyles.tableHeader}>Date</th>
                      <th className={analyticsStyles.tableHeader}>Bookings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(bookingsByDate).map(([date, count]) => (
                      <tr key={date}>
                        <td className={analyticsStyles.tableCell}>{format(parseISO(date), 'MMM d, yyyy')}</td>
                        <td className={analyticsStyles.tableCell}>{count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Revenue Over Time */}
            <div className={`${analyticsStyles.card} p-6`}>
              <h3 className={analyticsStyles.sectionTitle}>Revenue Over Time</h3>
              <div className="overflow-x-auto">
                <table className={analyticsStyles.table}>
                  <thead>
                    <tr>
                      <th className={analyticsStyles.tableHeader}>Date</th>
                      <th className={analyticsStyles.tableHeader}>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(revenueByDate).map(([date, amount]) => (
                      <tr key={date}>
                        <td className={analyticsStyles.tableCell}>{format(parseISO(date), 'MMM d, yyyy')}</td>
                        <td className={analyticsStyles.tableCell}>{formatPrice(amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className={`${analyticsStyles.card} p-6`}>
            <h3 className={analyticsStyles.sectionTitle}>Payment Methods</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">Online Payments</div>
                  <div className="text-xl font-semibold text-gray-900">{onlinePayments}</div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">Onsite Payments</div>
                  <div className="text-xl font-semibold text-gray-900">{onsitePayments}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Bookings */}
          <div className={`${analyticsStyles.card} p-6`}>
            <h3 className={analyticsStyles.sectionTitle}>Recent Bookings</h3>
            <div className="overflow-x-auto">
              <table className={analyticsStyles.table}>
                <thead>
                  <tr>
                    <th className={analyticsStyles.tableHeader}>Date</th>
                    <th className={analyticsStyles.tableHeader}>Guest</th>
                    <th className={analyticsStyles.tableHeader}>Amount</th>
                    <th className={analyticsStyles.tableHeader}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.slice(0, 5).map(b => (
                    <tr key={b.id}>
                      <td className={analyticsStyles.tableCell}>{format(parseISO(b.date), 'MMM d, yyyy')}</td>
                      <td className={analyticsStyles.tableCell}>{b.guestDetails.name}</td>
                      <td className={analyticsStyles.tableCell}>{formatPrice(b.price)}</td>
                      <td className={analyticsStyles.tableCell}>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          b.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {b.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaidPublicUse;
